import { AiUsageSettings } from "@/components/ai-usage/AiUsageSettings";
import { AppLayout } from "@/components/layout/app-layout";
import { FileTab } from "@/components/tabs/file-tab";
import { SynonymDictionary } from "@/components/verification/SynonymDictionary";
import { useAuth } from "@/hooks/use-auth";
import { ActivityLogPage } from "@/pages/activity-log/index";
import { HelpPage } from "@/pages/help/index";
import { LoginPage } from "@/pages/login/login-page";
import { RatesPage } from "@/pages/rates/index";
import { ReconciliationCreatePage } from "@/pages/reconciliation/create";
import { ReconciliationDetailPage } from "@/pages/reconciliation/detail";
import { ReconciliationListPage } from "@/pages/reconciliation/list";
import { ReconciliationResultPage } from "@/pages/reconciliation/result";
import { UploadPage } from "@/pages/upload/index";
import { UsersPage } from "@/pages/users/users-page";
import { VerificationPage } from "@/pages/verification/index";
import { useAuthStore } from "@/stores/auth-store";
import { useActiveTab, useTabActions } from "@/stores/tab-store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import "./index.css";

const ChangePasswordPage = lazy(() =>
	import("@/pages/login/change-password-page").then((m) => ({ default: m.ChangePasswordPage })),
);

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			retry: 1,
		},
	},
});

function AuthGuard({ children }: { children: React.ReactNode }) {
	const { isLoading } = useAuth();
	const { isAuthenticated, mustChangePassword } = useAuthStore();

	if (isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-slate-100">
				<p className="text-sm text-slate-500">Загрузка...</p>
			</div>
		);
	}

	if (!isAuthenticated) {
		return <LoginPage />;
	}

	if (mustChangePassword) {
		return (
			<Suspense
				fallback={
					<div className="flex min-h-screen items-center justify-center bg-slate-100">
						<p className="text-sm text-slate-500">Загрузка...</p>
					</div>
				}
			>
				<ChangePasswordPage />
			</Suspense>
		);
	}

	return <>{children}</>;
}

function AppContent() {
	const activeTab = useActiveTab();
	const { openFileTab } = useTabActions();

	if (!activeTab) return null;

	const renderActiveTab = () => {
		switch (activeTab.type) {
			case "upload":
				return (
					<UploadPage
						onOpenFileTab={(file) => {
							openFileTab(file.id, file.originalName);
						}}
					/>
				);
			case "rates":
				return <RatesPage />;
			case "verification":
				return <VerificationPage />;
			case "synonyms":
				return <SynonymDictionary />;
			case "activity-log":
				return <ActivityLogPage />;
			case "help":
				return <HelpPage />;
			case "file":
				return activeTab.fileId ? <FileTab fileId={activeTab.fileId} /> : null;
			case "users":
				return <UsersPage />;
			case "ai-settings":
				return <AiUsageSettings />;
			case "reconciliation-list":
				return <ReconciliationListPage />;
			case "reconciliation-create":
				return <ReconciliationCreatePage />;
			case "reconciliation-detail":
				return <ReconciliationDetailPage />;
			case "reconciliation-result":
				return <ReconciliationResultPage />;
			default:
				return null;
		}
	};

	return <AppLayout>{renderActiveTab()}</AppLayout>;
}

function App() {
	return (
		<AuthGuard>
			<AppContent />
		</AuthGuard>
	);
}

createRoot(document.getElementById("root") as HTMLElement).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<App />
			<Toaster position="bottom-right" />
		</QueryClientProvider>
	</StrictMode>,
);
